import { faArrowDown, faArrowLeft, faArrowRight, faArrowUp, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useApi } from '@hooks/useApi';
import { useConfirmedCallback } from '@hooks/useConfirmedCallback';
import { useModal } from '@hooks/useModal';
import { Button } from '@ui';
import { cx } from 'class-variance-authority';
import { get } from 'lodash';
import moment from 'moment/moment';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import useSWR from 'swr';
import Loader from '../Loader';
import { Step } from './types';

const StepForm = dynamic(() => import('./StepForm'));

interface Props {
    isOwner?: boolean;
    topicId?: number | null;
}

const StepList = ({ topicId, isOwner = false }: Props) => {
    const api = useApi();
    const activeStepRef = useRef<HTMLHeadingElement>(undefined);
    const [setModal, resetModal] = useModal();
    const { query, push } = useRouter();
    const activeStep = query.step ? parseInt(query.step as string) : null;
    const { isLoading, data, mutate } = useSWR<{ data: Step[] }>(topicId ? `academy/topic/${topicId}` : null);

    const handleShowStepForm = useCallback(
        (step?: Step) => {
            setModal({
                title: 'Schritt hinzufügen',
                content: (
                    <StepForm
                        step={step}
                        topicId={topicId}
                        callback={() => {
                            resetModal();
                            mutate();
                        }}
                    />
                ),
                show: true,
                size: 60
            });
        },
        [topicId]
    );

    const handleDeleteStep = useConfirmedCallback((step: Step) => {
        void api({
            url: `academy/step/${step.id}`,
            method: 'DELETE',
            onSuccess: () => mutate()
        });
    });

    const handleMoveStep = useCallback((source: number, target: number) => {
        if (!target || !source) return;
        void api({
            url: `academy/step/${source}/${target}`,
            method: 'GET',
            onSuccess: () => mutate()
        });
    }, []);

    useEffect(() => {
        if (!isLoading && activeStepRef.current && window) {
            const yOffset = -200;
            const top = activeStepRef.current.getBoundingClientRect().top + window.scrollY + yOffset;
            window.scrollTo({ top, behavior: 'smooth' });
        }
    }, [isLoading]);

    return (
        <Loader isLoading={isLoading}>
            {isOwner && (
                <Button
                    auto
                    icon={<FontAwesomeIcon icon={faPlus} />}
                    onPress={() => handleShowStepForm()}
                    className="tw-mb-4 tw-ml-auto"
                >
                    Schritt
                </Button>
            )}
            <div className="tw-space-y-4">
                {data?.data?.length > 0 ? (
                    data?.data?.map((step, index) => (
                        <div
                            onClick={() => push({ query: { ...query, step: step.id } }, undefined, { shallow: true })}
                            className={cx(
                                activeStep === step.id && 'tw-border-success',
                                'tw-card-flat tw-border-2 tw-border-solid tw-border-transparent'
                            )}
                            key={step.id}
                        >
                            {/* {step.files?.length > 0 && (
                                <Carousel>
                                    {step.files.map((image) => (
                                        <Carousel.Item key={image.id}>
                                            <div className="tw-relative tw-min-h-[350px] tw-w-full">
                                                <Image
                                                    className="tw-object-cover"
                                                    alt=""
                                                    src={`${process.env.NEXT_PUBLIC_API_HOST}file/${image.id}`}
                                                    layout="fill"
                                                />
                                            </div>
                                        </Carousel.Item>
                                    ))}
                                </Carousel>
                            )} */}
                            <div className="tw-p-4">
                                <h3
                                    className="tw-text-xl tw-font-bold"
                                    ref={activeStep === step.id ? activeStepRef : undefined}
                                >
                                    {step.title}
                                </h3>
                                <h5 className="tw-mb-2 tw-border-0 tw-border-b tw-border-solid tw-border-accents-3 tw-pb-2 tw-text-accents-6">
                                    {step.subtitle}
                                </h5>
                                <p className="tw-whitespace-pre-line">{step.content}</p>
                                <div className="tw-space-y-2">
                                    {step.links?.map((link) => (
                                        <Link href={link.href} key={link.id}>
                                            <div className="tw-flex tw-max-w-max tw-cursor-pointer tw-items-center tw-gap-4">
                                                <FontAwesomeIcon
                                                    icon={faArrowRight}
                                                    size="lg"
                                                    className="tw-text-success"
                                                />
                                                <div>
                                                    <span className="tw-text-md tw-block tw-font-bold">
                                                        {link.label}
                                                    </span>
                                                    {link.description && (
                                                        <span className="tw-block tw-text-xs tw-italic tw-text-accents-6">
                                                            {link.description}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                                <div className="tw-flex tw-items-center tw-justify-end tw-gap-8">
                                    {isOwner && (
                                        <div className="tw-flex tw-gap-4">
                                            <Button
                                                onPress={() =>
                                                    handleMoveStep(step.id, get(data?.data, index - 1, null)?.id)
                                                }
                                                size="xs"
                                            >
                                                <FontAwesomeIcon icon={faArrowUp} />
                                            </Button>
                                            <Button
                                                onPress={() =>
                                                    handleMoveStep(step.id, get(data?.data, index + 1, null)?.id)
                                                }
                                                size="xs"
                                            >
                                                <FontAwesomeIcon icon={faArrowDown} />
                                            </Button>
                                            <Button onPress={() => handleDeleteStep(step)} color="error" size="xs">
                                                Löschen
                                            </Button>
                                            <Button onPress={() => handleShowStepForm(step)} color="warning" size="xs">
                                                Bearbeiten
                                            </Button>
                                        </div>
                                    )}
                                    <span className="tw-text-right tw-text-xs tw-text-accents-6">
                                        Stand: {moment(step.updated_at).format('ll')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="tw-flex tw-items-center tw-justify-center tw-py-24">
                        <div className="tw-flex tw-items-center tw-gap-4">
                            <FontAwesomeIcon className="tw-text-accents-6" icon={faArrowLeft} size="lg" />
                            <span className="tw-text-xl tw-text-accents-6">Bitte wähle ein Thema aus</span>
                        </div>
                    </div>
                )}
            </div>
        </Loader>
    );
};

export default StepList;
